import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface FormField {
  id: string;
  name: string;
  label: string;
  type: string;
  required?: boolean;
  options?: Array<string | { label: string; value: string }>;
  placeholder?: string;
  defaultValue?: string;
}

interface ExtendedFormTemplate {
  id: number;
  name: string;
  description: string;
  schema: FormField[] | unknown;
  created_at: string;
  updated_at: string;
  version: number;
  is_latest: boolean;
  parent_template_id?: number;
}

interface TemplateEditorContextType {
  currentTemplate: ExtendedFormTemplate | null;
  setCurrentTemplate: (template: ExtendedFormTemplate | null) => void;
  clearCurrentTemplate: () => void;
}

const TemplateEditorContext = createContext<TemplateEditorContextType | undefined>(undefined);

export const useTemplateEditor = () => {
  const context = useContext(TemplateEditorContext);
  if (context === undefined) {
    throw new Error('useTemplateEditor must be used within a TemplateEditorProvider');
  }
  return context;
};

interface TemplateEditorProviderProps {
  children: React.ReactNode;
}

export const TemplateEditorProvider: React.FC<TemplateEditorProviderProps> = ({ children }) => {
  const [currentTemplate, setCurrentTemplate] = useState<ExtendedFormTemplate | null>(null);
  const location = useLocation();
  const previousPathRef = React.useRef(location.pathname);

  // Clear template when navigating away from edit pages
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;

    // Only clear if we're actually leaving the edit section
    // (e.g., going from /settings/templates/edit/123 to somewhere else)
    const wasInEditSection = previousPath.includes('/templates/edit/');
    const isInEditSection = currentPath.includes('/templates/edit/');

    // Clear only when leaving the edit section, not when entering it
    if (wasInEditSection && !isInEditSection) {
      setCurrentTemplate(null);
    }

    // Update the previous path ref
    previousPathRef.current = currentPath;
  }, [location.pathname]);

  const clearCurrentTemplate = () => {
    setCurrentTemplate(null);
  };

  const value: TemplateEditorContextType = {
    currentTemplate,
    setCurrentTemplate,
    clearCurrentTemplate
  };

  return <TemplateEditorContext.Provider value={value}>{children}</TemplateEditorContext.Provider>;
};
